import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../shared/theme';
import { s_PB, s_GB, s_PL } from '../shared/styles';
import { NOTIF_TYPES } from '../shared/constants';
import { deleteNotification, syncMarkNotifsRead, notifyUser } from '../shared/dbService';

const BASE = process.env.REACT_APP_API_URL || '/api';

function NotificationsDrawer({notifications, setNotifications, lang, onClose, onNav, friends, onAcceptFriend, onDeclineFriend, currentUserId, onGameInvite, onChatNotif}) {
  const nl = lang==="nl";
  const [tab, setTab] = useState("all");
  const unread = notifications.filter(n=>!n.read);
  const displayed = tab==="unread" ? unread : notifications;

  // Fetch fresh notifications from DB every time the drawer opens
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${BASE}/notifs/${currentUserId}`)
      .then(r => r.ok ? r.json() : null)
      .then(rows => {
        if (!Array.isArray(rows)) return;
        const fresh = rows.map(n => ({ ...n, _synced: true }));
        setNotifications(prev => {
          // Merge: keep local-only entries (no DB id match), prefer DB for duplicates
          const dbIds = new Set(fresh.map(n => n.id));
          const localOnly = prev.filter(n => !dbIds.has(n.id));
          return [...fresh, ...localOnly].sort((a, b) => (b.id || 0) - (a.id || 0));
        });
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markAll = () => { setNotifications(notifications.map(n=>({...n,read:true}))); syncMarkNotifsRead(); };
  const markRead = (id) => { setNotifications(notifications.map(n=>n.id===id?{...n,read:true}:n)); syncMarkNotifsRead(); };
  const dismiss = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
    deleteNotification(id);
  };

  return ReactDOM.createPortal(
    <div style={{position:"fixed",top:0,left:0,right:0,zIndex:1000}}>
      <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:199}}/>
      <div className="notifSlide" style={{position:"relative",zIndex:200,background:_T.bg2,borderBottom:`1px solid ${_T.cardBorderTop}`,borderRadius:"0 0 24px 24px",maxHeight:"80vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>
        <div style={{padding:"16px 18px 12px",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontFamily:"Georgia,serif",fontSize:18,color:C.white}}>🔔 {nl?"Meldingen":"Notifications"}</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {unread.length>0 && <button onClick={markAll} style={{fontSize:11,color:C.silver,background:"none",border:`1px solid ${_T.cardBorderBot}`,borderRadius:8,padding:"4px 10px",cursor:"pointer"}}>{nl?"Alles gelezen":"Mark all read"}</button>}
              <button onClick={onClose} style={{background:"none",border:"none",color:C.silver,cursor:"pointer",fontSize:20,lineHeight:1}}>×</button>
            </div>
          </div>
          <div style={{display:"flex",gap:4}}>
            {[{id:"all",label:nl?"Alle":"All",count:notifications.length},{id:"unread",label:nl?"Ongelezen":"Unread",count:unread.length}].map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{...s_PL(tab===t.id),margin:0,marginRight:6,fontSize:11,padding:"5px 12px"}}>
                {t.label} {t.count>0&&`(${t.count})`}
              </button>
            ))}
          </div>
        </div>
        {displayed.length===0 && (
          <div style={{padding:32,textAlign:"center",color:C.silver,fontSize:13}}>{nl?"Geen meldingen":"No notifications"}</div>
        )}
        {displayed.map(n => {
          const nt = NOTIF_TYPES[n.type] || {icon:"🔔",color:_T.accent};
          // n.from stores the sender's user ID (not display name) for friend_request type
          const fromId = n.from;
          return (
            <div key={n.id} onClick={()=>markRead(n.id)} style={{padding:"14px 18px",borderBottom:`1px solid ${_T.cardBorderBot}`,background:n.read?"transparent":`${nt.color}08`,display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{width:38,height:38,borderRadius:"50%",background:`${nt.color}20`,border:`1.5px solid ${nt.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{nt.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:n.read?C.silver2:C.white,fontSize:13,lineHeight:1.5,marginBottom:3}}>{n.text}</div>
                <div style={{fontSize:13,color:C.silver}}>{n.time}</div>
                {n.type==="friend_request" && fromId && !friends.includes(fromId) && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={e=>{e.stopPropagation();onAcceptFriend(fromId);dismiss(n.id);}} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>✓ {nl?"Accepteren":"Accept"}</button>
                    <button onClick={e=>{e.stopPropagation();onDeclineFriend(fromId);dismiss(n.id);}} style={{background:"transparent",border:`1px solid ${C.red}44`,color:C.red,borderRadius:9,padding:"6px 12px",cursor:"pointer",fontSize:12}}>✕</button>
                  </div>
                )}
                {n.type==="game_invite" && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={e=>{e.stopPropagation();dismiss(n.id);onClose();if(n.meta?.token&&onGameInvite){onGameInvite(n.meta);}else{onNav(3);}}} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>🎮 {nl?"Speel mee":"Join Room"}</button>
                    <button onClick={e=>{e.stopPropagation();dismiss(n.id);}} style={s_GB({padding:"6px 12px",fontSize:12,borderRadius:9})}>✕</button>
                  </div>
                )}
                {n.type==="exercise_invite" && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={e=>{e.stopPropagation();onClose();onNav(2);dismiss(n.id);}} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>▶ {nl?"Starten":"Start"}</button>
                    <button onClick={e=>{e.stopPropagation();dismiss(n.id);}} style={s_GB({padding:"6px 12px",fontSize:12,borderRadius:9})}>✕</button>
                  </div>
                )}
                {n.type==="group_invite" && n.meta?.groupId && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={async e=>{
                      e.stopPropagation();
                      await fetch(`${BASE}/groups/${n.meta.groupId}/members/${currentUserId}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({uid:currentUserId,status:'accepted'})});
                      if(n.meta.inviterUserId) notifyUser(n.meta.inviterUserId,'group_invite_accepted',n.from,nl?`${n.from} heeft je uitnodiging voor ${n.meta.groupName} geaccepteerd`:`${n.from} accepted your invitation to ${n.meta.groupName}`);
                      dismiss(n.id);
                    }} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>✓ {nl?"Accepteren":"Accept"}</button>
                    <button onClick={async e=>{
                      e.stopPropagation();
                      await fetch(`${BASE}/groups/${n.meta.groupId}/members/${currentUserId}`,{method:'DELETE'});
                      if(n.meta.inviterUserId) notifyUser(n.meta.inviterUserId,'group_invite_declined',n.from,nl?`${n.from} heeft je uitnodiging voor ${n.meta.groupName} geweigerd`:`${n.from} declined your invitation to ${n.meta.groupName}`);
                      dismiss(n.id);
                    }} style={s_GB({padding:"6px 12px",fontSize:12,borderRadius:9})}>✕ {nl?"Weigeren":"Decline"}</button>
                  </div>
                )}
                {n.type==="group_invite" && !n.meta?.groupId && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={e=>{e.stopPropagation();onClose();onNav(7);dismiss(n.id);}} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>{nl?"Bekijken":"View"}</button>
                    <button onClick={e=>{e.stopPropagation();dismiss(n.id);}} style={s_GB({padding:"6px 12px",fontSize:12,borderRadius:9})}>✕</button>
                  </div>
                )}
                {n.type==="chat_message" && (
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={e=>{e.stopPropagation();dismiss(n.id);if(onChatNotif)onChatNotif();else onNav(7);}} style={s_PB({padding:"6px 14px",fontSize:12,borderRadius:9})}>💬 {nl?"Bekijken":"View"}</button>
                  </div>
                )}
              </div>
              {!n.read && <div style={{width:8,height:8,borderRadius:"50%",background:nt.color,marginTop:4,flexShrink:0}}/>}
              <button onClick={e=>{e.stopPropagation();dismiss(n.id);}} style={{background:"none",border:"none",color:`${C.silver}66`,cursor:"pointer",fontSize:14,padding:"0 2px",flexShrink:0}}>×</button>
            </div>
          );
        })}
        <div style={{height:8}}/>
      </div>
    </div>,
    document.body
  );
}

export default NotificationsDrawer;
